import React from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import { toPersianNumber } from '../../utils/persianNumber';
import { M } from './monitorTokens';
import { MealThumb } from './MonitorParts';

export interface MealBreakdown {
    mealId: string;
    title: string;
    imageUrl?: string;
    total: number;
    delivered: number;
    remaining: number;
}

// Per-dish progress for the selected meal. Static and manually scrollable: the previous
// auto-scrolling ribbon hid dishes and moved constantly, which the kitchen could not read.
const MealProgressStrip: React.FC<{ items: MealBreakdown[] }> = ({ items }) => {
    if (items.length === 0) {
        return (
            <Box sx={{ flex: 2, minWidth: 0, display: 'grid', placeItems: 'center', bgcolor: M.surface, border: `1px dashed ${M.border}`, borderRadius: M.radiusCard, px: 2 }}>
                <Typography sx={{ fontFamily: M.font, color: M.textFaint }}>برای این وعده غذایی ثبت نشده است</Typography>
            </Box>
        );
    }

    return (
        <Box
            role="list"
            aria-label="وضعیت تحویل به تفکیک غذا"
            sx={{
                flex: 2, minWidth: 0, display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5,
                scrollSnapType: 'x proximity',
                '&::-webkit-scrollbar': { height: 6 },
                '&::-webkit-scrollbar-thumb': { bgcolor: M.borderStrong, borderRadius: 3 },
            }}
        >
            {items.map((item) => {
                const pct = item.total ? (item.delivered / item.total) * 100 : 0;
                const done = item.remaining === 0;
                return (
                    <Box
                        key={item.mealId}
                        role="listitem"
                        sx={{
                            minWidth: 250, flex: '1 0 250px', scrollSnapAlign: 'start',
                            bgcolor: M.surface, border: `1px solid ${done ? `${M.success}55` : M.border}`,
                            borderRadius: M.radiusCard, boxShadow: M.cardShadow, p: 1.5,
                            display: 'flex', alignItems: 'center', gap: 1.5,
                        }}
                    >
                        <MealThumb imageUrl={item.imageUrl} title={item.title} size={56} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography noWrap title={item.title} sx={{ fontFamily: M.font, fontWeight: 700, color: M.text, fontSize: '1rem' }}>
                                {item.title}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, mt: 0.25 }}>
                                <Typography sx={{ fontFamily: M.font, color: M.textMuted, fontSize: '0.85rem' }}>
                                    {toPersianNumber(item.delivered)} از {toPersianNumber(item.total)} تحویل
                                </Typography>
                                <Typography sx={{ fontFamily: M.font, fontWeight: 800, fontSize: '1.05rem', color: done ? M.successText : M.warningText, fontVariantNumeric: 'tabular-nums' }}>
                                    {done ? 'تمام' : `${toPersianNumber(item.remaining)} مانده`}
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={pct}
                                aria-label={`${item.title}: ${toPersianNumber(item.delivered)} از ${toPersianNumber(item.total)}`}
                                sx={{ mt: 0.75, height: 5, borderRadius: 3, bgcolor: M.surfaceHover, '& .MuiLinearProgress-bar': { bgcolor: done ? M.success : M.primary, borderRadius: 3 } }}
                            />
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};

export default MealProgressStrip;
